/**
 * API client - fetch wrapper. Sends the session cookie (`credentials: "include"`).
 * Staff-CRM endpoints (`/api/admin/crm/*`, `/api/admin/ariregister/*`) are
 * PLATFORM-scoped (`requirePlatform`) - the server resolves the host site itself,
 * so unlike web_admin we do NOT send `X-Site-Id`.
 *
 * The base URL is deliberately the RELATIVE `/api` (same origin), not
 * VITE_API_URL: nginx proxies `crm.webadmin.ee/api/` to Fastify. That keeps the
 * session cookie first-party - a cross-site call to api.husly.app would drop it,
 * because the cookie is `SameSite=Lax`.
 *
 * NOTE: no SSO with web_admin/web_account when the CRM is served from
 * crm.webadmin.ee. The API derives the cookie domain from the request host
 * (`sessionCookieDomain`), so this host gets `Domain=webadmin.ee` while
 * admin/account get `Domain=husly.app`. Separate domains = separate logins,
 * which is why web_crm ships its own LoginPage. The nginx `/api/` block for this
 * host must therefore forward `Host $host`; hardcoding `Host api.husly.app` makes
 * the API emit `Domain=husly.app`, the browser rejects that cookie outright, and
 * every authenticated call answers 401.
 */
export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown) {
    super(typeof body === "object" && body && "error" in body ? String((body as { error: unknown }).error) : `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

/** The relative base every call hangs off. Exported because one caller cannot
 *  go through `req` at all: an `EventSource` builds its own URL and has no
 *  business hardcoding a second copy of this (see `shellSignals.ts`). */
export const API_BASE = "/api";

async function req<T>(path: string, init: RequestInit = {}, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { ...(init.headers as Record<string, string>) };
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    credentials: "include",
    body: body !== undefined ? JSON.stringify(body) : init.body,
  });

  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, data);
  return data as T;
}

/**
 * A multipart POST, for the one thing JSON cannot carry: a file.
 *
 * `Content-Type` is deliberately NOT set. The browser has to write it itself,
 * because a multipart body is only parseable alongside the boundary token it
 * generated - naming the type by hand drops that token and the server reads the
 * upload as an empty request.
 */
async function upload<T>(path: string, form: FormData, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { method: "POST", body: form, credentials: "include", signal });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, data);
  return data as T;
}

export const api = {
  upload,
  // `signal` is how a screen says it no longer wants an answer. Only worth
  // reaching for where the request costs the server something real - the IMAP
  // reads hold the mailbox's single connection slot, so a folder the operator
  // has already clicked past must not keep it.
  get: <T>(path: string, signal?: AbortSignal) => req<T>(path, { method: "GET", signal }),
  post: <T>(path: string, body?: unknown) => req<T>(path, { method: "POST" }, body),
  patch: <T>(path: string, body?: unknown) => req<T>(path, { method: "PATCH" }, body),
  put: <T>(path: string, body?: unknown) => req<T>(path, { method: "PUT" }, body),
  // A body on a DELETE, for the routes that ask for an explicit confirmation
  // rather than treating the method as one. Optional, so every existing caller
  // sends none and behaves exactly as before.
  del: <T>(path: string, body?: unknown) => req<T>(path, { method: "DELETE" }, body),
};
